import React from 'react';
import './PrintableContractAppendix.css'; // Assuming a similar CSS file structure
import { format } from 'date-fns';

// Interfaces based on data structure likely needed from controller
interface AppendixItem {
  structureCode: string;
  structureType: string; // Added for clarity
  location: string;
  startDate: Date | string;
  endDate: Date | string;
  price: number; // Assuming 'price' from controller logic is unit price
  total: number; // Assuming 'total' from controller logic is final price for item
}

interface ClientInfo {
  name: string;
  director?: string | null;
}

interface ContractInfo {
  contractNumber: string;
  startDate: Date | string; // Assuming contract start date is relevant
}

interface AppendixData {
  contract: ContractInfo;
  client: ClientInfo;
  appendixNumber?: string; // Appendix might have its own number/date
  appendixDate?: Date | string;
  items: AppendixItem[];
  totalSum: number;
  paymentTerms?: string | null; // Include payment terms if needed
}

interface PrintableContractAppendixProps {
  appendixData: AppendixData;
}

// Helper to format dates
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'dd.MM.yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper to format currency
const formatCurrency = (value: number | null | undefined): string => {
  if (value === undefined || value === null) return '0 ₸';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const PrintableContractAppendix: React.FC<PrintableContractAppendixProps> = ({ appendixData }) => {
  if (!appendixData) {
    return <div className="printable-container">Ошибка: Данные для приложения к договору не найдены.</div>;
  }

  const {
    contract,
    client,
    appendixNumber, // Use if available
    appendixDate,   // Use if available
    items,
    totalSum,
    paymentTerms,
  } = appendixData;

  // Determine the title date (appendix date or contract start date)
  const titleDate = appendixDate ? formatDate(appendixDate) : formatDate(contract.startDate);
  const titleNumber = appendixNumber ? `№ ${appendixNumber}` : ''; // Add appendix number if exists

  return (
    <div className="printable-container appendix-container">
      <header className="printable-header">
        <h1>Приложение {titleNumber}</h1>
        <p>к Договору № {contract.contractNumber} от {formatDate(contract.startDate)}</p>
        {appendixDate && <p>Дата приложения: {formatDate(appendixDate)}</p>}
      </header>

      <section className="parties">
        <p>
          ТОО "Рекламное агентство SignageAgency", именуемое в дальнейшем "Исполнитель",
          в лице директора Иванова И.И., действующего на основании Устава, с одной стороны,
        </p>
        <p>
          и {client.name}, именуемый в дальнейшем "Заказчик",
          в лице {client.director || 'уполномоченного представителя'},
          действующего на основании Устава, с другой стороны,
        </p>
        <p>согласовали следующую спецификацию:</p>
      </section>

      <section className="specification-table">
        <h2>Спецификация</h2>
        <table>
          <thead>
            <tr>
              <th>№</th>
              <th>Наименование (Код)</th>
              <th>Расположение</th>
              <th>Период размещения</th>
              <th>Цена, ₸</th>
              <th>Итого, ₸</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.structureCode} ({item.structureType})</td>
                <td>{item.location}</td>
                <td>{formatDate(item.startDate)} - {formatDate(item.endDate)}</td>
                <td>{formatCurrency(item.price)}</td>
                <td>{formatCurrency(item.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>ИТОГО:</td>
              <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(totalSum)}</td>
            </tr>
          </tfoot>
        </table>
      </section>

      <section className="payment-terms">
        <h2>Порядок оплаты</h2>
        <p>{paymentTerms || 'Оплата производится в следующем порядке: 50% предоплата в течение 3 банковских дней с момента подписания Договора, 50% в течение 5 банковских дней с момента подписания акта выполненных работ.'}</p>
      </section>

      <section className="signatures">
        <h2>Подписи сторон</h2>
        <div className="signature-blocks">
          <div className="signature-block">
            <p><strong>Исполнитель:</strong></p>
            <p>ТОО "Рекламное агентство SignageAgency"</p>
            <br /><br />
            <p>____________________</p>
            <p>(Подпись)</p>
            <p>М.П.</p>
          </div>
          <div className="signature-block">
            <p><strong>Заказчик:</strong></p>
            <p>{client.name}</p>
            <br /><br />
            <p>____________________</p>
            <p>(Подпись)</p>
            <p>М.П.</p>
          </div>
        </div>
      </section>

      <footer className="printable-footer">
        {/* Footer content if needed */}
      </footer>
    </div>
  );
};

export default PrintableContractAppendix;
